import history from 'utils/appHistory'

import {
  ADD_ALARM,
  END_ALARM_LAUNCH,
  START_ALARM_LAUNCH,
  SET_ALARMS,
  REMOVE_ALARM,
  UPDATE_ALARM,
} from './actionTypes'
import { launchPlayer, endPlaylistPlay } from './playerActions'
import { AWAKENING, daysOfWeekMap } from 'utils/constants'

export const initializeAlarms = () => (dispatch, getState) => {
  const initializedAlarms = JSON.parse(
    localStorage.getItem(`alarms_${getState().auth.userId}`)
  )

  if (initializedAlarms)
    return dispatch({
      type: SET_ALARMS,
      payload: initializedAlarms,
    })
}

export const addAlarm = (newAlarm) => (dispatch, getState) => {
  const alarmsItem = `alarms_${getState().auth.userId}`
  newAlarm.alarmInfo.id = Math.floor(Date.now() / 1000)
  newAlarm.alarmInfo.dateLastRan = ''

  const alarms = JSON.parse(localStorage.getItem(alarmsItem)) || []
  const alarmsWithAdded = [newAlarm, ...alarms]
  addAndroidAlarm(alarmsWithAdded)

  localStorage.setItem(alarmsItem, JSON.stringify(alarmsWithAdded))

  return dispatch({
    type: ADD_ALARM,
    payload: newAlarm,
  })
}

const addAndroidAlarm = (alarm) => {
  if (!window.cordova && window.device?.platform !== 'Android') return

  const alarms = []

  for (const a of alarm) {
    const alarmHours = parseInt(a.alarmInfo.time.slice(0, 2))
    const alarmMinutes = parseInt(a.alarmInfo.time.slice(3))

    // Start alarm 15 min earlier than set time:
    const hourEarlier = alarmMinutes < 15

    const hoursToCheck = hourEarlier
      ? alarmHours === 0
        ? 23
        : alarmHours - 1
      : alarmHours

    // If adding 45 minutes to a time with a decremented hour,
    // so same as subtracting 45 min from original time
    // Otherwise, add 15 min since need to check 15 min before alarm
    const minutesToCheck = hourEarlier ? 45 + alarmMinutes : alarmMinutes - 15
    const days = []

    a.alarmInfo.days.forEach((value, index) => {
      if (value) days.push(daysOfWeekMap.get(index))
    })

    alarms.push({
      id: a.alarmInfo.id,
      type: 'daylist',
      skipOnRunning: true,
      time: {
        hour: hoursToCheck,
        minute: minutesToCheck,
      },
      extra: {
        message: 'Music alarm triggered!',
      },
      days,
      message: 'Music alarm has triggered on specified time!',
      startInBackground: true,
    })
  }

  window.wakeuptimer.wakeup(successCallback, errorCallback, {
    alarms,
  })

  const successCallback = (result) => {
    if (result.type === 'wakeup') {
      console.log('Wakeup alarm detected--' + result.extra)
    } else if (result.type === 'set') {
      console.log('Wakeup alarm set--' + result)
    } else {
      console.log('wakeup unhandled type (' + result.type + ')')
    }
  }
  const errorCallback = (result) => {
    if (result.type === 'wakeup') {
      console.log('Wakeup alarm detected--' + result.extra)
    } else if (result.type === 'set') {
      console.log('Wakeup alarm set--' + result)
    } else {
      console.log('Wakeup unhandled type (' + result.type + ')')
    }
  }
}

export const removeAlarm = (alarmToRemoveId) => (dispatch, getState) => {
  const alarmsItem = `alarms_${getState().auth.userId}`

  const alarms = JSON.parse(localStorage.getItem(alarmsItem)) || []

  const alarmsWithRemoved = alarms.filter(
    ({ alarmInfo }) => alarmInfo.id !== alarmToRemoveId
  )

  addAndroidAlarm(alarmsWithRemoved)
  localStorage.setItem(alarmsItem, JSON.stringify(alarmsWithRemoved))

  return dispatch({
    type: REMOVE_ALARM,
    payload: alarmToRemoveId,
  })
}

const setAsRanToday = (alarmId) => (dispatch, getState) => {
  const alarmsItem = `alarms_${getState().auth.userId}`

  const alarms = JSON.parse(localStorage.getItem(alarmsItem)) || []

  const alarmToUpdateIndex = alarms.findIndex(({ alarmInfo }) => alarmInfo.id === alarmId)
  if (alarmToUpdateIndex === -1) return

  const alarmToUpdate = {
    alarmInfo: { ...alarms[alarmToUpdateIndex].alarmInfo },
    songId: alarms[alarmToUpdateIndex].songId,
  }

  alarmToUpdate.alarmInfo.dateLastRan = new Date().toLocaleDateString()

  alarms[alarmToUpdateIndex] = alarmToUpdate
  localStorage.setItem(alarmsItem, JSON.stringify(alarms))

  dispatch({ type: UPDATE_ALARM, payload: alarmToUpdate })
}

export const startAlarmLaunch = (alarmId) => (dispatch) => {
  dispatch(setAsRanToday(alarmId))

  if (window.cordova && window.device?.platform === 'Android') {
    window.screenLocker.unlock(successCallback, errorCallback)

    const successCallback = () => console.log('Screen unlock successfully')
    const errorCallback = (e) => console.log('Error: ' + e)
  }

  return dispatch({ type: START_ALARM_LAUNCH, payload: alarmId })
}

export const endAlarmLaunch = () => (dispatch) => {
  if (window.cordova && window.device?.platform === 'Android') {
    window.screenLocker.lock(successCallback, errorCallback)

    const successCallback = () => console.log('Screen locked successfully')
    const errorCallback = (e) => console.log('Error: ' + e)
  }

  return dispatch({ type: END_ALARM_LAUNCH })
}

export const editAlarm =
  ({ alarmInfo, songId }, editingAlarmId) =>
  (dispatch, getState) => {
    const alarmsItem = `alarms_${getState().auth.userId}`

    const alarms = JSON.parse(localStorage.getItem(alarmsItem)) || []

    const alarmToUpdateIndex = alarms.findIndex(
      (anAlarm) => anAlarm.alarmInfo.id === editingAlarmId
    )
    if (alarmToUpdateIndex === -1) return

    // Reset already ran state
    alarmInfo.dateLastRan = ''
    // Maintain same id for alarm edited
    alarmInfo.id = editingAlarmId

    const alarmToUpdate = { alarmInfo, songId }
    alarms[alarmToUpdateIndex] = alarmToUpdate
    addAndroidAlarm(alarms)
    localStorage.setItem(alarmsItem, JSON.stringify(alarms))

    dispatch({ type: UPDATE_ALARM, payload: alarmToUpdate })
  }

export const continuouslyCheckForAlarm = () => (dispatch, getState) => {
  const intervalId = setInterval(() => {
    const alarms = getState().alarm.alarms

    // Only launch alarm if player not already playing
    const alreadyPlaying = getState().player.playState
    if (alreadyPlaying || !alarms.length) return

    const currentTime = new Date()
    const currentHours = currentTime.getHours()
    const currentMinutes = currentTime.getMinutes()
    const currentWeekDayNumber = currentTime.getDay()

    for (const { alarmInfo, songId } of alarms) {
      // Skip this alarm if it is not set for current weekday
      if (!alarmInfo.days[currentWeekDayNumber]) continue

      // If already ran today, skip (prevents launching same alarm within same minute)
      if (alarmInfo.dateLastRan === new Date().toLocaleDateString()) continue

      const alarmHours = parseInt(alarmInfo.time.slice(0, 2))
      const alarmMinutes = parseInt(alarmInfo.time.slice(3))

      // Start alarm 20 min earlier than set time:
      const hourEarlier = alarmMinutes < 15

      const hoursToCheck = hourEarlier
        ? alarmHours === 0
          ? 23
          : alarmHours - 1
        : alarmHours

      // If adding 45 minutes to a time with a decremented hour,
      // so same as subtracting 45 min from original time
      // Otherwise, add 15 min since need to check 15 min before alarm
      const minutesToCheck = hourEarlier ? 45 + alarmMinutes : alarmMinutes - 15

      if (currentHours === hoursToCheck && currentMinutes === minutesToCheck) {
        const songSessionInfo = {
          // Alarms always launch with session type AWAKENING
          songId,
          indicationType: AWAKENING,
        }

        dispatch(startAlarmLaunch(alarmInfo.id))
        dispatch(launchPlayer(songSessionInfo))
        // In case player is in playlist mode and paused, reset playlist state
        dispatch(endPlaylistPlay())

        if (history.location.pathname !== '/player') history.push('/player')

        // No need to launch any other alarms
        break
      }
    }
  }, 2000)

  return intervalId
}
